import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { brandSelectionsService } from '../api/brand-selections';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { BrandSelection } from '@/types/brand';

// Resolved picks are static reference data — a product's name and colour value
// never change under us. Cache them for the session.
const STATIC_STALE_TIME = 24 * 60 * 60 * 1000;

/**
 * Resolve a Brands selection into the rows that print it.
 *
 * Order is preserved so the rows line up with the stored selection, and a pick
 * whose product has been deleted from the catalogue simply does not come back —
 * callers show their own "loading / gone" state for a token with no entry.
 */
export function useBrandSelections(tokens: string[] | null | undefined) {
  const list = useMemo(() => tokens ?? [], [tokens]);
  // The key is the selection in pick order, not sorted: the API answers in the
  // order it was asked, and that order is what the rows display.
  const key = list.join(',');

  const query = useQuery({
    queryKey: cacheKeys.brandSelections.resolve(list),
    queryFn: () => brandSelectionsService.resolve(list),
    enabled: list.length > 0,
    staleTime: STATIC_STALE_TIME,
    gcTime: STATIC_STALE_TIME,
  });

  const entries = useMemo(() => query.data ?? [], [query.data]);

  const byToken = useMemo(() => {
    const map = new Map<string, BrandSelection>();
    for (const entry of entries) {
      map.set(entry.id, entry);
    }
    return map;
  }, [entries]);

  const hexes = useMemo(
    () =>
      entries
        .map((entry) => entry.attributes.hex)
        .filter((hex): hex is string => !!hex),
    [entries]
  );

  return { ...query, key, entries, byToken, hexes };
}
